// src/components/CalendarIntegrationPanel.js
//
// Admin console panel for a site's calendar integration (rendered by
// OrgManagementPage at /manage).
//
// Replaces what used to require editing appsscript/research-calendar.gs and
// rebuilding the app: which calendar a site books into, its timezone, and its
// room list. Onboarding a site is now filling this in.
//
// Connection details (endpoint URL, shared secret) can be SET here but never
// READ back: they live in a `private` subcollection Firestore rules deny to
// every client, are written through the tenant-admin-gated `calendarConnection`
// Cloud Function, and the status call returns booleans plus a truncated hint
// rather than values. Rotating a secret means setting a new one, not viewing
// the old one — so a stolen admin session can't exfiltrate a credential that
// writes to the calendar.
//
// Everything is per-SITE and addressed EXPLICITLY (tenantId + siteId), never
// via the active-site helpers — an admin on /manage is frequently looking at a
// site other than the one they're working in.

import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarRange,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Lock,
  Plug,
  ShieldAlert,
  Activity,
} from "lucide-react";
import {
  CALENDAR_ROOM_PALETTE,
  paletteEntry,
  validateCalendarConfig,
  fetchCalendarConfigForSite,
  saveCalendarConfigForSite,
  fetchCalendarConnectionStatus,
  setCalendarConnection,
  testCalendarConnection,
} from "../services/calendarConfigService";

const input =
  "rounded-lg bg-white/[0.05] border border-white/10 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors duration-200 hover:border-white/20 focus:border-purple-400/50 focus:ring-1 focus:ring-purple-400/30";
const labelCls =
  "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";

// A short list covers essentially every US research site; the field is a free
// text input backed by a datalist, so any IANA zone can still be typed.
const COMMON_TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const PROVIDERS = [
  { id: "apps-script", label: "Google Calendar (via Apps Script)" },
  { id: "none", label: "Not connected" },
];

export default function CalendarIntegrationPanel({ tenantId, sites = [], busy }) {
  const [siteId, setSiteId] = useState("");
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState("");

  // Connection (endpoint URL + shared secret). Write-only: `conn` is a status
  // summary of booleans, never the stored values.
  const [conn, setConn] = useState(null);
  const [connUrl, setConnUrl] = useState("");
  const [connSecret, setConnSecret] = useState("");
  const [connBusy, setConnBusy] = useState(false);
  const [testResult, setTestResult] = useState("");

  // Default to the first site once the list arrives, and re-default if the
  // admin switches to a company whose sites don't include the current pick.
  useEffect(() => {
    if (!sites.length) {
      setSiteId("");
      return;
    }
    if (!sites.some((s) => s.id === siteId)) setSiteId(sites[0].id);
  }, [sites, siteId]);

  const load = useCallback(async () => {
    if (!tenantId || !siteId) {
      setCfg(null);
      return;
    }
    setLoading(true);
    setErr("");
    setSaved("");
    setTestResult("");
    setConnUrl("");
    setConnSecret("");
    try {
      setCfg(await fetchCalendarConfigForSite(tenantId, siteId));
    } catch (e) {
      // Deliberately NOT falling back to defaults here — saving defaults over a
      // config that merely failed to load would wipe a site's real settings.
      setCfg(null);
      setErr(`Couldn't load this site's calendar settings: ${e.message}`);
    } finally {
      setLoading(false);
    }
    // Connection status is a separate, non-fatal call: an admin should still be
    // able to edit rooms if the status endpoint is unreachable.
    try {
      setConn(await fetchCalendarConnectionStatus(tenantId, siteId));
    } catch {
      setConn(null);
    }
  }, [tenantId, siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (fields) => {
    setCfg((c) => ({ ...c, ...fields }));
    setSaved("");
  };

  // "Other" is structural: the booking modals treat it as "type a free-text
  // room name". It's rendered separately and can't be renamed or removed.
  const editableRooms = (cfg?.rooms || []).filter((r) => r.name !== "Other");
  const otherRoom = (cfg?.rooms || []).find((r) => r.name === "Other");

  const setRooms = (nextEditable, nextOther = otherRoom) =>
    patch({ rooms: [...nextEditable, nextOther].filter(Boolean) });

  const updateRoom = (index, fields) =>
    setRooms(
      editableRooms.map((r, i) => (i === index ? { ...r, ...fields } : r))
    );

  const addRoom = () => {
    // Pick the first palette color not already in use so a new room is
    // visually distinct without the admin having to think about it.
    const used = new Set((cfg?.rooms || []).map((r) => String(r.colorId)));
    const next =
      CALENDAR_ROOM_PALETTE.find((p) => !used.has(p.colorId)) ||
      CALENDAR_ROOM_PALETTE[editableRooms.length % CALENDAR_ROOM_PALETTE.length];
    setRooms([
      ...editableRooms,
      { name: "", colorId: next.colorId, fill: next.fill, accent: next.accent, label: next.label },
    ]);
  };

  const removeRoom = (index) =>
    setRooms(editableRooms.filter((_, i) => i !== index));

  const recolor = (index, colorId, isOther = false) => {
    const p = paletteEntry(colorId);
    const fields = { colorId: p.colorId, fill: p.fill, accent: p.accent, label: p.label };
    if (isOther) setRooms(editableRooms, { ...otherRoom, ...fields });
    else updateRoom(index, fields);
  };

  const handleSave = async () => {
    if (!cfg) return;
    const named = editableRooms.filter((r) => r.name.trim());
    const next = { ...cfg, rooms: [...named, otherRoom].filter(Boolean) };

    const problem = validateCalendarConfig(next);
    if (problem) {
      setErr(problem);
      return;
    }

    setSaving(true);
    setErr("");
    try {
      const written = await saveCalendarConfigForSite(tenantId, siteId, next);
      setCfg({ ...written, exists: true });
      setSaved("Saved. New bookings at this site use these settings.");
    } catch (e) {
      setErr(
        `Couldn't save: ${e.message}. Only company admins can change integration settings.`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConnection = async () => {
    setConnBusy(true);
    setErr("");
    setTestResult("");
    try {
      const status = await setCalendarConnection(tenantId, siteId, {
        scriptUrl: connUrl.trim(),
        // undefined leaves the stored secret alone — so an admin updating only
        // the URL doesn't have to re-enter (or know) the existing secret.
        sharedSecret: connSecret ? connSecret : undefined,
      });
      setConn(status);
      setConnUrl("");
      setConnSecret("");
      setSaved("Connection saved.");
    } catch (e) {
      setErr(`Couldn't save the connection: ${e.message}`);
    } finally {
      setConnBusy(false);
    }
  };

  const handleTest = async () => {
    setConnBusy(true);
    setErr("");
    setTestResult("");
    try {
      const r = await testCalendarConnection(tenantId, siteId);
      setTestResult(
        `Connected. Read ${r.count} event${r.count === 1 ? "" : "s"} for today.`
      );
    } catch (e) {
      setErr(`Test failed: ${e.message}`);
    } finally {
      setConnBusy(false);
    }
  };

  const colorSelect = (room, index, isOther = false) => (
    <div className="flex items-center gap-2">
      <span
        className="w-4 h-4 rounded-full shrink-0"
        style={{
          backgroundColor: room.fill,
          boxShadow: `inset 0 0 0 1.5px ${room.accent}`,
        }}
      />
      <select
        className={`${input} appearance-none pr-7 py-1.5 text-xs`}
        value={String(room.colorId)}
        onChange={(e) => recolor(index, e.target.value, isOther)}
      >
        {CALENDAR_ROOM_PALETTE.map((p) => (
          <option key={p.colorId} value={p.colorId} className="bg-[#0b0820]">
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <h2 className="font-semibold mb-1 flex items-center gap-2">
        <CalendarRange className="w-4 h-4 text-amber-300" /> Calendar
      </h2>
      <p className="text-xs text-slate-400 mb-4">
        Which calendar this site books into, and the rooms coordinators can pick
        from. These used to be hardcoded — a new site now just fills this in.
      </p>

      {/* Site picker */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="block">
          <span className={labelCls}>Site</span>
          <select
            className={`${input} appearance-none pr-8`}
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            disabled={!sites.length}
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id} className="bg-[#0b0820]">
                {s.name || s.id}
              </option>
            ))}
            {!sites.length && <option value="">No sites yet</option>}
          </select>
        </label>
        {loading && (
          <span className="inline-flex items-center gap-1.5 pb-2 text-xs text-slate-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </span>
        )}
      </div>

      <AnimatePresence>
        {err && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={{ opacity: 1, height: "auto", marginBottom: 12 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          </motion.div>
        )}
        {saved && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={{ opacity: 1, height: "auto", marginBottom: 12 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-start gap-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{saved}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {cfg && (
        <>
          {!cfg.exists && (
            <div className="mb-4 rounded-lg border border-amber-400/25 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-200/90">
              This site has no calendar settings yet — the values below are
              starting defaults and aren't saved until you hit Save. Until then
              the site can't book onto a calendar.
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            <label className="block">
              <span className={labelCls}>Connection</span>
              <select
                className={`${input} appearance-none pr-8 w-full`}
                value={cfg.provider}
                onChange={(e) => patch({ provider: e.target.value })}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.id} value={p.id} className="bg-[#0b0820]">
                    {p.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className={labelCls}>Display name</span>
              <input
                className={`${input} w-full`}
                value={cfg.label}
                placeholder="Research Calendar"
                onChange={(e) => patch({ label: e.target.value })}
              />
            </label>

            <label className="block sm:col-span-2">
              <span className={labelCls}>Google Calendar ID</span>
              <input
                className={`${input} w-full font-mono text-xs`}
                value={cfg.calendarId}
                placeholder="c_xxxxx@group.calendar.google.com"
                onChange={(e) => patch({ calendarId: e.target.value })}
              />
              <span className="mt-1 block text-[11px] text-slate-500">
                Google Calendar → the calendar's Settings → “Integrate calendar”
                → Calendar ID.
              </span>
            </label>

            <label className="block">
              <span className={labelCls}>Time zone</span>
              <input
                className={`${input} w-full`}
                list="regx-tz-options"
                value={cfg.timeZone}
                onChange={(e) => patch({ timeZone: e.target.value })}
              />
              <datalist id="regx-tz-options">
                {COMMON_TIME_ZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
            </label>

            {/* Patient names in event titles. Default ON because that's what
                every existing event looks like — changing it silently would
                rewrite how a live calendar reads. */}
            <div className="sm:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-purple-500"
                  checked={!!cfg.includePatientName}
                  onChange={(e) => patch({ includePatientName: e.target.checked })}
                />
                <span>
                  <span className="block text-sm text-slate-200">
                    Show patient names in Google Calendar event titles
                  </span>
                  <span className="mt-0.5 block text-[11px] text-slate-500">
                    Off: events read{" "}
                    <span className="font-mono text-slate-400">
                      [{editableRooms[0]?.name || "Room"}] Subject 4F2A91 — Study — V3
                    </span>
                    . RegX still shows the real name to staff inside the portal.
                  </span>
                </span>
              </label>

              {cfg.includePatientName && (
                <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-amber-400/25 bg-amber-500/[0.07] px-2.5 py-2 text-[11px] text-amber-200/90">
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Anyone who can see this calendar can read patient names next
                    to their study and visit. That's usually the first thing a
                    hospital privacy review flags — turn it off unless the site
                    has a reason to keep it.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Rooms */}
          <div className="mb-4">
            <span className={labelCls}>Rooms</span>
            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {editableRooms.map((room, i) => (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <input
                      className={`${input} flex-1 min-w-[10rem]`}
                      value={room.name}
                      placeholder="Room name (e.g. Suite B)"
                      onChange={(e) => updateRoom(i, { name: e.target.value })}
                    />
                    {colorSelect(room, i)}
                    <button
                      onClick={() => removeRoom(i)}
                      className="rounded-lg border border-white/10 p-2 text-red-300/80 hover:text-red-200 hover:border-red-400/30 transition-colors"
                      title="Remove room"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>

              {otherRoom && (
                <div className="flex flex-wrap items-center gap-2 opacity-80">
                  <div
                    className={`${input} flex-1 min-w-[10rem] flex items-center gap-2 text-slate-400`}
                    title="Always available — lets a coordinator type a one-off location"
                  >
                    <Lock className="w-3 h-3 shrink-0" /> Other
                  </div>
                  {colorSelect(otherRoom, -1, true)}
                  <span className="w-[34px]" aria-hidden="true" />
                </div>
              )}
            </div>

            <button
              onClick={addRoom}
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-purple-300 hover:text-purple-200 hover:underline transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add room
            </button>
          </div>

          {/* Connection — write-only. The stored URL and secret are never sent
              back to the browser; `conn` is booleans plus a truncated hint. */}
          <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 text-sm text-slate-200">
                <Plug className="w-3.5 h-3.5 text-emerald-300" /> Connection
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                  conn?.hasUrl
                    ? "border-emerald-400/30 bg-emerald-500/15 text-emerald-200"
                    : "border-slate-500/30 bg-slate-500/10 text-slate-400"
                }`}
              >
                {conn?.hasUrl ? "CONFIGURED" : "NOT CONFIGURED"}
                {conn?.hasUrl && !conn?.hasSecret ? " · NO SECRET" : ""}
              </span>
            </div>

            {conn?.hasUrl && (
              <p className="mb-2 font-mono text-[11px] text-slate-500 break-all">
                {conn.urlHint}
                {conn.updatedBy ? (
                  <span className="font-sans"> · set by {conn.updatedBy}</span>
                ) : null}
              </p>
            )}

            <div className="grid sm:grid-cols-2 gap-2">
              <input
                className={`${input} w-full font-mono text-xs`}
                value={connUrl}
                placeholder={
                  conn?.hasUrl
                    ? "Replace endpoint URL (leave blank to keep)"
                    : "https://script.google.com/macros/s/…/exec"
                }
                onChange={(e) => setConnUrl(e.target.value)}
              />
              <input
                className={`${input} w-full font-mono text-xs`}
                type="password"
                autoComplete="new-password"
                value={connSecret}
                placeholder={
                  conn?.hasSecret
                    ? "Replace shared secret (leave blank to keep)"
                    : "Shared secret (optional until you lock the script)"
                }
                onChange={(e) => setConnSecret(e.target.value)}
              />
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-3">
              <button
                onClick={handleSaveConnection}
                disabled={connBusy || !connUrl.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-emerald-400/30 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {connBusy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plug className="w-3.5 h-3.5" />
                )}
                Save connection
              </button>
              <button
                onClick={handleTest}
                disabled={connBusy || !conn?.hasUrl}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-purple-400/30 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
                title="Reads today's events — never creates one"
              >
                <Activity className="w-3.5 h-3.5" /> Test connection
              </button>
              {testResult && (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {testResult}
                </span>
              )}
            </div>

            <p className="mt-2 text-[11px] text-slate-500">
              Values are stored server-side and can't be read back — rotating
              means setting a new one, not viewing the old one. The test is
              read-only and never creates an event on a live calendar.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleSave}
              disabled={saving || busy || loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3.5 py-2 text-sm font-medium text-white shadow-lg shadow-purple-900/30 hover:brightness-110 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save calendar settings
            </motion.button>
            <span className="text-[11px] text-slate-500">
              Existing events are never changed — this only affects new bookings.
            </span>
          </div>

          <p className="mt-4 flex items-start gap-1.5 text-[11px] text-slate-500">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" />
            Only company admins can change anything on this page, and changes
            affect new bookings only — no existing calendar event is touched.
          </p>
        </>
      )}
    </div>
  );
}
